import { useMemo, useState } from 'react';
import MemberFilters from './components/MemberFilters';
import MemberTable from './components/MemberTable';
import MemberDrawer from './components/MemberDrawer';
import MemberFormModal from './components/MemberFormModal';
import KpiCard from '../components/KpiCard';
import { members as initialMembers } from '@/mocks/members';
import type { Member } from '@/types/member';

export default function MembersPage() {
  const [memberList, setMemberList] = useState<Member[]>(initialMembers);
  const [query, setQuery] = useState('');
  const [tier, setTier] = useState('All');
  const [platform, setPlatform] = useState('All platforms');
  const [selected, setSelected] = useState<Member | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return memberList.filter((member) => {
      const matchesQuery = !q || member.name.toLowerCase().includes(q) || member.email.toLowerCase().includes(q);
      const matchesTier = tier === 'All' || member.tier === tier;
      const matchesPlatform = platform === 'All platforms' || member.platform === platform;
      return matchesQuery && matchesTier && matchesPlatform;
    });
  }, [memberList, query, tier, platform]);

  const totalBalance = memberList.reduce((sum, member) => sum + member.balance, 0);
  const eliteCount = memberList.filter((member) => member.tier === 'Elite').length;
  const activeCount = memberList.filter((member) => member.status === 'active').length;

  const handleAddMember = (newMember: Member) => {
    setMemberList((prev) => [newMember, ...prev]);
  };

  const stats = [
    {
      id: 'total',
      label: 'Total members',
      value: memberList.length.toLocaleString(),
      icon: 'ri-group-line',
      tone: 'primary' as const,
      note: 'All registered users',
    },
    {
      id: 'active',
      label: 'Active members',
      value: activeCount.toLocaleString(),
      icon: 'ri-user-follow-line',
      tone: 'accent' as const,
      note: `${((activeCount / (memberList.length || 1)) * 100).toFixed(0)}% engagement rate`,
    },
    {
      id: 'elite',
      label: 'Elite tier',
      value: eliteCount.toLocaleString(),
      icon: 'ri-vip-crown-line',
      tone: 'secondary' as const,
      note: 'Top tier VIP members',
    },
    {
      id: 'balance',
      label: 'Points outstanding',
      value: totalBalance.toLocaleString(),
      icon: 'ri-coins-line',
      tone: 'background' as const,
      note: 'Total active wallet pool',
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-2xl text-foreground-900 tracking-tight">Members</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Every member across your connected platforms.</p>
        </div>
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-medium shadow-sm hover:from-primary-600 hover:to-primary-700 hover:shadow hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-user-add-line text-base"></i> Add member
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <KpiCard
            key={stat.id}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            tone={stat.tone}
            note={stat.note}
          />
        ))}
      </div>

      <MemberFilters
        query={query}
        onQueryChange={setQuery}
        tier={tier}
        onTierChange={setTier}
        platform={platform}
        onPlatformChange={setPlatform}
        resultCount={filtered.length}
        total={memberList.length}
      />

      <MemberTable data={filtered} onSelect={setSelected} />

      <MemberDrawer member={selected} onClose={() => setSelected(null)} />

      <MemberFormModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddMember}
      />
    </div>
  );
}
